import type {
  BladeDataSource,
  BladeListData,
  BladeEntityData,
  BladeSearchData,
  BladeSearchHit,
} from '../BladeDataSource';
import type { BladeDescriptor } from '../types';

// Static sample source for the backendless preview (/shell-preview). No network;
// returns the same shapes the live @hub/api source does, so the primitives don't
// know the difference. No PHI (invented sample data).

const SAMPLE_ROWS = [
  { id: 'CL-1023-4829', title: 'Cascade Family Health', meta: '$1,240.00 · 32d' },
  { id: 'CL-1023-5510', title: 'Riverbend Pediatrics', meta: '$680.50 · 18d' },
  { id: 'CL-1023-6701', title: 'Summit Orthopedics', meta: '$3,905.00 · 47d' },
  { id: 'CL-1023-7244', title: 'Lakeview Cardiology', meta: '$512.75 · 9d' },
  { id: 'CL-1023-8890', title: 'Meadowbrook Clinic', meta: '$2,118.20 · 25d' },
];

const SAMPLE_ENTITY: Omit<BladeEntityData, 'title'> = {
  badge: 'Active',
  stats: [
    { label: 'Open balance', value: '$48,210' },
    { label: 'Open claims', value: '37' },
    { label: 'Avg days in A/R', value: '29' },
  ],
  fields: [
    { label: 'NPI', value: '1487654321', mono: true },
    { label: 'Tax ID', value: '93-1847261', mono: true },
    { label: 'Primary payer', value: 'Blue Cross Blue Shield' },
    { label: 'Address', value: '120 Birch St, Portland, OR' },
    { label: 'Specialty', value: 'Family medicine' },
    // Sample of a server-redacted identifier field (HUB-357) so the
    // "Restricted" placeholder is visible in the backendless preview.
    { label: 'patient_name', value: '', restricted: true },
  ],
  related: [
    { id: 'CL-1023-4829', meta: '$1,240.00 · 32d' },
    { id: 'CL-1023-5510', meta: '$680.50 · 18d' },
    { id: 'CL-1023-6701', meta: '$3,905.00 · 47d' },
  ],
};

// Deterministic sample hits for the backendless preview / tests (the shapes the
// live search RPC returns; formerly the search primitive's inline sample set).
const SAMPLE_HITS: BladeSearchHit[] = [
  { id: 'c1', title: 'Cascade Family Health', type: 'Client' },
  { id: 'c2', title: 'Riverbend Pediatrics', type: 'Client' },
  { id: 'c3', title: 'Summit Orthopedics', type: 'Client' },
  { id: 'CL-1023-4829', title: 'CL-1023-4829', type: 'Claim' },
  { id: 'CL-1023-5510', title: 'CL-1023-5510', type: 'Claim' },
  { id: '99213', title: '99213 — Office visit, established', type: 'Code' },
];

export const mockSource: BladeDataSource = {
  async fetchList(): Promise<BladeListData> {
    return { rows: SAMPLE_ROWS };
  },
  async fetchEntity(descriptor: BladeDescriptor): Promise<BladeEntityData> {
    return { title: descriptor.title || descriptor.entityId || 'Record', ...SAMPLE_ENTITY };
  },
  async fetchSearch(descriptor: BladeDescriptor): Promise<BladeSearchData> {
    const needle = (descriptor.params?.q ?? '').trim().toLowerCase();
    return {
      hits: SAMPLE_HITS.filter(
        (h) => h.title.toLowerCase().includes(needle) || h.id.toLowerCase().includes(needle),
      ),
    };
  },
};
